import type { AppliedFrame } from './applied-frame';
import { fromBaseRecord } from '../compile/from-base-context';
import { readItemField } from '../compile/item-field-reader';
import { encounterCreatureField } from '../../shared/array-types';
import { statusRows } from '../statuses/expiry';

/** Resolve tombstones against the pre-apply tree: creature status cleanup changes
 * actors, while an item light (or an unknown owner) still changes the floor. */
export function appliedInventoryUnchanged(frame: AppliedFrame, hosts: Iterable<unknown>): boolean {
    if (frame.new_inventory?.length || frame.deletes.some(row => row.table === 'inventory')) return false;
    const upper = (value: unknown): string => String(value ?? '').toUpperCase();
    const members = new Set([...Object.keys(frame.member_revs), ...frame.new_entities.map(entity => entity.entity_id)].map(upper));
    const kits = new Set(frame.new_entities.flatMap(entity => Array.isArray(entity.base.inventory)
        ? entity.base.inventory.filter(fromBaseRecord).map(node => upper(readItemField(node.inventory as Record<string, unknown>, 'inventory_id'))) : []));
    const owners = new Map<string, string>();
    const seen = new WeakSet<object>();
    const visit = (host: unknown): void => {
        if (!fromBaseRecord(host) || seen.has(host)) return;
        seen.add(host);
        const id = upper(encounterCreatureField(host.encounter_creatures, 'encounter_creature_id'));
        if (id) members.add(id);
        for (const status of [...statusRows(host, 'target'), ...statusRows(host, 'caster')])
            owners.set('statuses:' + upper(readItemField(status, 'status_id')), upper(readItemField(status, 'status_target_id')));
        if (Array.isArray(host.charges)) for (const charge of host.charges) if (fromBaseRecord(charge))
            owners.set('charges:' + upper(readItemField(charge, 'charge_id')), upper(readItemField(charge, 'charge_holder')));
        for (const key of ['encounter_creatures_arr', 'inventory', 'inventory_in_container', 'encounter_inventories_arr'])
            if (Array.isArray(host[key])) for (const child of host[key]) visit(child);
        if (fromBaseRecord(host._entity_base)) visit(host._entity_base.base);
    };
    for (const host of hosts) visit(host);
    for (const { table, row_id, row } of frame.rows) if (table === 'statuses' || table === 'charges')
        owners.set(`${table}:${upper(row_id)}`, upper(readItemField(row, table === 'statuses' ? 'status_target_id' : 'charge_holder')));
    if (frame.deletes.some(({ table, row_id }) => (table === 'statuses' || table === 'charges')
        && !members.has(owners.get(`${table}:${upper(row_id)}`) ?? ''))) return false;
    const actorOwned = (id: unknown): boolean => members.has(upper(id)) || kits.has(upper(id));
    return frame.rows.every(({ table, row_id, row }) => (table !== 'inventory' || kits.has(upper(row_id)))
        && (table !== 'statuses' || actorOwned(readItemField(row, 'status_target_id')))
        && (table !== 'charges' || actorOwned(readItemField(row, 'charge_holder'))));
}
